/**
 * Dependency-free vertical bar chart (inline SVG) drawn in real pixels: the
 * container width is measured with a ResizeObserver and the SVG renders 1 unit
 * = 1px at a fixed height, so marks and text never scale with the card (the
 * previous viewBox scaling blew the chart up to ~3x its design size on wide
 * screens). Bars are capped at 24px thick with 4px rounded tops anchored to a
 * recessive baseline; axis labels thin out automatically when bands get narrow
 * (the most recent point is always labeled). Every nonzero bar is
 * direct-labeled with its value; when bands get too narrow for the widest
 * label, values thin out on the same end-anchored grid as the axis labels (the
 * max bar always keeps its label). Every band is a full-height hover target
 * exposing its exact value via a <title> — so values never depend on color.
 * Blue single series → no legend (the card title names it).
 */

import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";

export interface BarDatum {
  /** Raw month key `YYYY-MM` or day `YYYY-MM-DD` (used as React key). */
  month: string;
  /** Short axis label (locale short month or day). */
  label: string;
  count: number;
}

export interface BarChartProps {
  data: BarDatum[];
  title: string;
  formatNumber: (n: number) => string;
  /** Bar color; defaults to the primary series. */
  color?: string;
}

const BAR_MAX_W = 24;
const GAP = 2;
const PAD_TOP = 16;
const PLOT_H = 112;
const AXIS_H = 18;
const HEIGHT = PAD_TOP + PLOT_H + AXIS_H;
/** Minimum horizontal room an axis label needs before labels thin out. */
const LABEL_MIN_PX = 40;

/** Rounded-TOP rectangle path anchored to the baseline (bottom corners square). */
function roundedTopRect(x: number, y: number, w: number, h: number, radius: number): string {
  const r = Math.min(radius, w / 2, h);
  return [
    `M${x},${y + h}`,
    `L${x},${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `L${x + w - r},${y}`,
    `Q${x + w},${y} ${x + w},${y + r}`,
    `L${x + w},${y + h}`,
    "Z",
  ].join(" ");
}

export function BarChart({ data, title, formatNumber, color = "var(--chart-series)" }: BarChartProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w !== undefined) setWidth(Math.floor(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = data.length;
  const baselineY = PAD_TOP + PLOT_H;
  const max = Math.max(...data.map((d) => d.count), 1);
  const maxIndex = data.findIndex((d) => d.count === max && d.count > 0);
  const band = n > 0 ? width / n : 0;
  const barW = Math.max(Math.min(BAR_MAX_W, band - GAP), 2);
  const labelStep = Math.max(1, Math.ceil(LABEL_MIN_PX / Math.max(band, 1)));
  // Room the widest value needs (~6.5px/char at the 11px label size) before
  // value labels thin out like the axis labels do.
  const valueMinPx = Math.max(...data.map((d) => formatNumber(d.count).length)) * 6.5 + 6;
  const valueStep = Math.max(1, Math.ceil(valueMinPx / Math.max(band, 1)));

  return (
    <div className="dpp-bars" ref={ref} style={{ height: HEIGHT }}>
      {width > 0 && n > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label={title}>
          <title>{title}</title>

          {/* Recessive baseline — no heavy gridlines. */}
          <line x1={0} y1={baselineY} x2={width} y2={baselineY} stroke="var(--chart-grid)" strokeWidth={1} />

          {data.map((d, i) => {
            const cx = i * band + band / 2;
            const h = d.count > 0 ? Math.max((d.count / max) * PLOT_H, 3) : 0;
            const y = baselineY - h;
            // Keep edge labels inside the SVG (centered text would clip at the borders).
            const labelX = Math.min(Math.max(cx, LABEL_MIN_PX / 2), width - LABEL_MIN_PX / 2);
            return (
              <g key={d.month}>
                <title>{`${d.label}: ${formatNumber(d.count)}`}</title>
                {/* Full-height hit target: the hover tooltip never requires landing on the mark. */}
                <rect x={i * band} y={0} width={band} height={HEIGHT} fill="transparent" />
                {h > 0 && (
                  <path
                    d={roundedTopRect(cx - barW / 2, y, barW, h, 4)}
                    fill={color}
                    // Staggers the grow-from-the-axis mount animation (charts.css).
                    style={{ "--i": i } as CSSProperties}
                  />
                )}
                {d.count > 0 && ((n - 1 - i) % valueStep === 0 || i === maxIndex) && (
                  <text className="dpp-bars__value" x={labelX} y={y - 5} textAnchor="middle">
                    {formatNumber(d.count)}
                  </text>
                )}
                {(n - 1 - i) % labelStep === 0 && (
                  <text className="dpp-bars__label" x={labelX} y={HEIGHT - 5} textAnchor="middle">
                    {d.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}
